import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Loader2, ImageOff } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { http, apiError, imgSrc } from "@/lib/shop";

const TYPES = ["image/jpeg", "image/png", "image/webp"];

export const PhotoField = ({ value, onChange }) => {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const [showUrl, setShowUrl] = useState(false);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!TYPES.includes(file.type)) return toast.error("Please choose a JPG, PNG or WebP photo.");
    if (file.size > 8 * 1024 * 1024) return toast.error("Photo is too large (max 8 MB).");
    setBusy(true);
    try {
      const body = new FormData();
      body.append("file", file);
      const { data } = await http.post("/uploads/product-image", body);
      onChange(data.image_url);
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid gap-2" data-testid="pf-photo-field">
      <Label className="text-xs font-semibold">Product photo</Label>
      <div className="flex items-center gap-4">
        <div className="h-20 w-20 shrink-0 rounded-xl overflow-hidden bg-slate-100 grid place-items-center" data-testid="pf-photo-preview">
          {value ? <img src={imgSrc(value)} alt="Product preview" className="h-full w-full object-cover" /> : <ImageOff className="h-6 w-6 text-slate-400" />}
        </div>
        <div className="flex flex-col gap-2">
          <input ref={input} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={upload} data-testid="pf-photo-input" />
          <button type="button" onClick={() => input.current?.click()} disabled={busy} className="inline-flex items-center justify-center gap-2 rounded-full bg-ink px-5 py-2.5 text-sm font-bold text-white hover:bg-brand disabled:opacity-60 transition-colors" data-testid="pf-upload-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} {busy ? "Uploading…" : value ? "Change photo" : "Upload Photo"}
          </button>
          <button type="button" onClick={() => setShowUrl(!showUrl)} className="text-xs font-semibold text-slate-500 hover:text-brand text-left" data-testid="pf-url-toggle">
            {showUrl ? "Hide image link" : "Or paste an image link instead"}
          </button>
        </div>
      </div>
      {showUrl && <Input placeholder="https://…" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid="pf-image" />}
    </div>
  );
};
